import { useEffect } from 'react';

const getScrollParent = (node: HTMLElement | null): HTMLElement => {
  if (!node || node === document.body || node === document.documentElement) {
    return (document.scrollingElement as HTMLElement) || document.body;
  }
  
  const overflowY = globalThis.getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
  
  if (isScrollable && node.scrollHeight > node.clientHeight) {
    return node;
  }
  
  return getScrollParent(node.parentElement);
};


export function useDropdownAutoScroll<T extends HTMLElement>(
  isOpen: boolean,
  elementRef: { current: T | null },
  trigger?: unknown,
  offset: number = 12
) {
  useEffect(() => {
    if (!isOpen || !elementRef.current) return;

    const element = elementRef.current;
    let timeoutId: ReturnType<typeof setTimeout>;

    const adjustScroll = () => {
      clearTimeout(timeoutId);
      
      timeoutId = setTimeout(() => {
        if (!elementRef.current) return;
        
        const scrollParent = getScrollParent(element);
        const rect = element.getBoundingClientRect();
        
        const parentBottom = (scrollParent === document.body || scrollParent === document.documentElement)
          ? (window.innerHeight || document.documentElement.clientHeight)
          : scrollParent.getBoundingClientRect().bottom;

        if (rect.bottom > parentBottom) {
          const pixelsToScroll = (rect.bottom - parentBottom) + offset;
          
          scrollParent.scrollBy({ 
            top: pixelsToScroll, 
            behavior: 'smooth' 
          });
        }
      }, 100);
    };

    adjustScroll();

    const resizeObserver = new ResizeObserver(() => {
      adjustScroll();
    });
    
    resizeObserver.observe(element);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
    
  }, [isOpen, elementRef, trigger, offset]); 
}